// Orden de Despacho (pestaña de TRABAJO, docs/110). Sin id: el PLANIFICADOR (NV confirmadas
// disponibles → selección múltiple → camión, chofer, ruta → crear OD). Con id: la FICHA de la OD.
// Camino SIMPLE (pedido de Rodrigo, 2026-10-13): manifiesto → faltantes → documentos y hoja de
// rendición → despacho (el camión se carga en el stock al salir) → rendición → cierre. Las OD que
// iban por el camino largo (preparación, validación, aprobación) lo terminan con sus botones.
import { clientesContraEntrega, HojaCobranzaImpresa } from './HojaCobranza';
import { imprimirPdf } from '../comun/imprimirPdf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, pedirArchivo } from '../api/cliente';
import { useNota, useVendedores } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { DatosDoc, EncabezadoDoc, Tarjeta } from '../compras/FichaDocumento';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Imprimir } from '../comun/Imprimir';
import { Modal } from '../comun/Modal';
import { decimal, fecha, fechaHora, NOMBRE_CONDICION, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';
import {
  clavesLog,
  CAMINO_LARGO,
  NOMBRE_ESTADO_OD,
  PASOS_OD,
  PASOS_OD_SIMPLE,
  useAccionOd,
  useChoferes,
  useDisponibles,
  useDocumentosOd,
  useManifiesto,
  useOd,
  useRevisionCarga,
  useRutas,
  useVehiculos,
  type DiferenciaCarga,
  type EstadoOd,
  type FiltroPlanificador,
  type LineaOd,
  type NotaDisponible,
  type NotaOd,
  type Od,
  type ProductoManifiesto,
} from './datos';
import { NOMBRE_TIPO_DTE } from '../shell/BuscadorGlobal';
import { HojaRendicionImpresa, ManifiestoImpreso } from './ImpresosOd';
import { hoyIso } from '../ventas/comercial';
import { coincide } from '../comun/coincide';

export function OrdenDespacho({
  odId,
  alCreada,
  alAbrirNota,
}: {
  odId?: string | undefined;
  alCreada: (odId: string, numero: number) => void;
  alAbrirNota?: (notaId: string, numero: string) => void;
}) {
  return odId ? (
    <FichaOd odId={odId} {...(alAbrirNota ? { alAbrirNota } : {})} />
  ) : (
    <Planificador alCreada={alCreada} {...(alAbrirNota ? { alAbrirNota } : {})} />
  );
}

// ================================================================ planificador

/** Kilos con a lo más un decimal, en formato chileno. */
function kg(v: string | null | undefined): string {
  const n = Number(v ?? 0);
  return n.toLocaleString('es-CL', { maximumFractionDigits: 1 });
}

function useZonas(empresaId: string) {
  return useQuery({
    queryKey: ['emp', empresaId, 'zonas'],
    queryFn: () =>
      api.get<Array<{ id: string; nombre: string; codigo?: string }>>('/ventas/zonas', {
        query: { empresaId },
      }),
    staleTime: 10 * 60_000,
  });
}

/**
 * El detalle de una NV sin salir del planificador (pedido de Rodrigo, 2026-10-26): qué lleva, a qué
 * precio y sus observaciones, para decidir si va en esta ODD. Desde aquí se elige o se quita.
 */
function DetalleNotaPlan({
  nota,
  elegida,
  alAlternar,
  alAbrirNota,
  alCerrar,
}: {
  nota: NotaDisponible;
  elegida: boolean;
  alAlternar: () => void;
  alAbrirNota?: ((notaId: string, numero: string) => void) | undefined;
  alCerrar: () => void;
}) {
  const { empresa } = useSesion();
  const q = useNota(empresa!.id, nota.notaId);
  const n = q.data;
  const pendiente = new Map((n?.cumplimiento ?? []).map((c) => [c.numero, c]));
  return (
    <Modal
      titulo={`${nota.numeroVisible} · ${nota.clienteRazonSocial}`}
      ancho="ancho"
      alCerrar={alCerrar}
      pie={
        <>
          {alAbrirNota && (
            <button
              type="button"
              onClick={() => {
                alAbrirNota(nota.notaId, nota.numeroVisible);
                alCerrar();
              }}
            >
              Abrir la nota completa
            </button>
          )}
          <button type="button" onClick={alCerrar}>
            Cerrar
          </button>
          <button
            type="button"
            className="primario"
            data-autofoco
            onClick={() => {
              alAlternar();
              alCerrar();
            }}
          >
            {elegida ? 'Quitar de la ODD' : 'Elegir para la ODD'}
          </button>
        </>
      }
    >
      <DatosDoc
        datos={[
          { e: 'Fecha', v: fecha(nota.fechaNegocio) },
          { e: 'Entrega', v: fecha(nota.fechaEntrega ?? nota.fechaNegocio) },
          { e: 'Dirección', v: nota.direccionEntrega ?? '—' },
          { e: 'Zona', v: nota.zonaNombre ?? '—' },
          { e: 'Condición', v: NOMBRE_CONDICION[nota.condicionPago] ?? nota.condicionPago },
          { e: 'Kg', v: kg(nota.pesoKg) },
        ]}
      />
      {n?.cabecera.observaciones && (
        <p className="ayuda">
          <b>Observaciones:</b> {n.cabecera.observaciones}
        </p>
      )}
      {q.isError && <PanelError error={q.error} />}
      {q.isLoading ? (
        <p className="apagado">Cargando el detalle…</p>
      ) : (
        n && (
          <table className="grilla compacta" aria-label={`Detalle de ${nota.numeroVisible}`}>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Descripción</th>
                <th>Present.</th>
                <th className="der">Cantidad</th>
                <th className="der">Pendiente (u)</th>
                <th className="der">Precio</th>
                <th className="der">Total</th>
              </tr>
            </thead>
            <tbody>
              {n.lineas.map((l) => (
                <tr key={l.numero}>
                  <td className="mono">{l.sku}</td>
                  <td>{l.descripcion}</td>
                  <td>{l.presentacionCodigo}</td>
                  <td className="der">{decimal(l.cantidad)}</td>
                  <td className="der">
                    {decimal(pendiente.get(l.numero)?.pendienteBase ?? l.cantidadBase)}
                  </td>
                  <td className="der">{pesos(l.precioAplicado)}</td>
                  <td className="der">{pesos(l.bruto)}</td>
                </tr>
              ))}
              {n.cargos.map((c, i) => (
                <tr key={`cargo-${i}`} className="apagado">
                  <td />
                  <td colSpan={5}>{c.descripcion}</td>
                  <td className="der">{pesos(c.bruto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={6} className="der">
                  Total
                </th>
                <th className="der">{pesos(n.totales.bruto)}</th>
              </tr>
            </tfoot>
          </table>
        )
      )}
    </Modal>
  );
}

function Planificador({
  alCreada,
  alAbrirNota,
}: {
  alCreada: (odId: string, numero: number) => void;
  alAbrirNota?: (notaId: string, numero: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const bodegas = useBodegas(empresaId);
  // La OD parte de una BODEGA (como «bodegas de interés» en Random): camiones y mermas no son origen.
  const origenes = bodegas.lista.filter((b) => b.tipo === 'BODEGA');
  const [bodegaElegida, setBodega] = useState('');
  const bodegaId = bodegaElegida || origenes[0]?.id || '';
  const [f, setF] = useState<Omit<FiltroPlanificador, 'ubicacionId'>>({});
  const [productoTexto, setProductoTexto] = useState('');
  const filtro = { ...f, ubicacionId: bodegaId };
  const q = useDisponibles(empresaId, filtro);
  const zonas = useZonas(empresaId);
  const vendedores = useVendedores(empresaId);
  const vehiculos = useVehiculos(empresaId);
  const choferes = useChoferes(empresaId);
  const rutas = useRutas(empresaId);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [detalle, setDetalle] = useState<NotaDisponible | null>(null);
  const [vehiculoId, setVehiculo] = useState('');
  const [choferId, setChofer] = useState('');
  const [recaudadorId, setRecaudador] = useState('');
  const [nombreOd, setNombreOd] = useState('');
  const [rutaId, setRuta] = useState('');
  const [fechaOd, setFechaOd] = useState(hoyIso());
  // Las notas que ya están en una OD vigente no se ofrecen de nuevo (se ven en esa OD), ni las que
  // esperan la NC de lo que el cliente rechazó.
  const filas = (q.data ?? []).filter((n) => n.enOd === null && !n.esperaNc);
  const elegidas = filas.filter((n) => sel.has(n.notaId));
  const unidades = elegidas.reduce((s, n) => s + Number(n.unidadesPendientes), 0);
  const kilos = elegidas.reduce((s, n) => s + Number(n.pesoKg ?? 0), 0);
  const sinPeso = elegidas.reduce((s, n) => s + (n.productosSinPeso ?? 0), 0);
  const camion = (vehiculos.data ?? []).find((v) => v.id === vehiculoId);
  const capacidad = camion?.capacidadKg ? Number(camion.capacidadKg) : null;
  const ocupacion = capacidad ? Math.round((kilos / capacidad) * 100) : null;
  const monto = elegidas.reduce((s, n) => s + Number(n.bruto ?? 0), 0);
  const nombreVendedor = new Map((vendedores.data ?? []).map((v) => [v.id, v.nombre]));
  const cambiar = (c: Partial<typeof f>) => setF((x) => ({ ...x, ...c }));
  const alternar = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const crear = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        bodegaId,
        fechaNegocio: fechaOd,
        notaIds: elegidas.map((n) => n.notaId),
        ...(vehiculoId ? { vehiculoId } : {}),
        ...(choferId ? { choferId } : {}),
        ...(recaudadorId ? { recaudadorId } : {}),
        ...(nombreOd.trim() ? { nombre: nombreOd.trim() } : {}),
        ...(rutaId ? { rutaId } : {}),
      };
      return api.post<{ odId: string; numero: number }>('/logistica/ordenes', cuerpo, {
        idempotencia: intentos.clave('crear-od', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setSel(new Set());
      await qc.invalidateQueries({ queryKey: clavesLog.logistica(empresaId) });
      alCreada(r.odId, r.numero);
    },
    onError: (e) => intentos.cerrar(e),
  });
  const columnas: Columna<NotaDisponible>[] = [
    {
      id: 'sel',
      titulo: '',
      celda: (n) => (
        <input
          type="checkbox"
          aria-label={`Elegir ${n.numeroVisible}`}
          checked={sel.has(n.notaId)}
          onChange={() => alternar(n.notaId)}
        />
      ),
      ancho: '2rem',
    },
    {
      id: 'nv',
      titulo: 'NV',
      celda: (n) => (
        <button
          type="button"
          className="enlace mono"
          title="Ver el detalle de la nota"
          onClick={() => setDetalle(n)}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {n.numeroVisible}
        </button>
      ),
      ancho: '9rem',
    },
    { id: 'fecha', titulo: 'Fecha', celda: (n) => fecha(n.fechaNegocio), ancho: '7rem' },
    {
      id: 'entrega',
      titulo: 'Entrega',
      celda: (n) =>
        n.fechaEntrega && n.fechaEntrega !== n.fechaNegocio ? (
          <b>{fecha(n.fechaEntrega)}</b>
        ) : (
          <span className="apagado">{fecha(n.fechaEntrega ?? n.fechaNegocio)}</span>
        ),
      ancho: '7rem',
    },
    { id: 'cliente', titulo: 'Cliente', celda: (n) => n.clienteRazonSocial },
    {
      id: 'dir',
      titulo: 'Dirección',
      celda: (n) => <span className="apagado">{n.direccionEntrega ?? '—'}</span>,
    },
    { id: 'zona', titulo: 'Zona', celda: (n) => n.zonaNombre ?? '—', ancho: '8rem' },
    {
      id: 'vend',
      titulo: 'Vendedor',
      celda: (n) => (n.vendedorId ? (nombreVendedor.get(n.vendedorId) ?? '—') : '—'),
      ancho: '9rem',
    },
    {
      id: 'cond',
      titulo: 'Condición',
      celda: (n) => NOMBRE_CONDICION[n.condicionPago] ?? n.condicionPago,
      ancho: '7rem',
    },
    {
      id: 'unid',
      titulo: 'Unidades',
      celda: (n) => decimal(n.unidadesPendientes),
      alinear: 'der',
      ancho: '6rem',
    },
    {
      id: 'kg',
      titulo: 'Kg',
      celda: (n) => (
        <span
          title={
            n.productosSinPeso
              ? `${n.productosSinPeso} producto(s) sin peso en el catálogo: no se cuentan`
              : undefined
          }
        >
          {kg(n.pesoKg)}
          {n.productosSinPeso ? ' *' : ''}
        </span>
      ),
      alinear: 'der',
      ancho: '6rem',
    },
    {
      id: 'monto',
      titulo: 'Monto',
      celda: (n) => (n.bruto ? pesos(n.bruto) : '—'),
      alinear: 'der',
      ancho: '8rem',
    },
  ];
  const boton = puede('logistica.od.crear') ? (
    <button
      type="button"
      className="primario"
      disabled={!elegidas.length || crear.isPending}
      onClick={() => crear.mutate()}
    >
      Crear ODD con {elegidas.length} nota(s)
    </button>
  ) : (
    <span className="apagado">Sin permiso para planificar órdenes de despacho</span>
  );
  const choferesActivos = (choferes.data ?? []).filter((c) => c.activo);
  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="od-planificador">
      <EncabezadoDoc
        sigla="ODD"
        migas="Operaciones › Orden de Despacho"
        titulo="Nueva orden de despacho"
        sub="Elige las notas de venta, el camión y el personal de ruta."
      />
      {crear.isError && (
        <div className="doc-aviso tono-malo">
          <PanelError error={crear.error} />
        </div>
      )}
      <div className="od-plan">
        <div className="doc-principal od-plan-izq">
          <Tarjeta titulo="Buscar notas de venta" etiqueta="Filtros">
            <div className="barra-filtros">
              <SelectorBodega bodegas={origenes} valor={bodegaId} alCambiar={setBodega} />
              <label className="campo">
                <span className="etiqueta">Desde</span>
                <input
                  type="date"
                  aria-label="Desde"
                  value={f.desde ?? ''}
                  onChange={(e) => cambiar({ desde: e.target.value || undefined })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Hasta</span>
                <input
                  type="date"
                  aria-label="Hasta"
                  value={f.hasta ?? ''}
                  onChange={(e) => cambiar({ hasta: e.target.value || undefined })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Entrega desde</span>
                <input
                  type="date"
                  aria-label="Entrega desde"
                  value={f.entregaDesde ?? ''}
                  onChange={(e) => cambiar({ entregaDesde: e.target.value || undefined })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Entrega hasta</span>
                <input
                  type="date"
                  aria-label="Entrega hasta"
                  value={f.entregaHasta ?? ''}
                  onChange={(e) => cambiar({ entregaHasta: e.target.value || undefined })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Ruta</span>
                <select
                  aria-label="Filtrar por ruta"
                  value={f.rutaId ?? ''}
                  onChange={(e) => cambiar({ rutaId: e.target.value || undefined })}
                >
                  <option value="">Todas</option>
                  {(rutas.data ?? [])
                    .filter((x) => x.activo)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.codigo} · {x.nombre}
                      </option>
                    ))}
                </select>
              </label>
              <label className="campo">
                <span className="etiqueta">Zona</span>
                <select
                  aria-label="Zona"
                  value={f.zonaId ?? ''}
                  onChange={(e) => cambiar({ zonaId: e.target.value || undefined })}
                >
                  <option value="">Todas</option>
                  {(zonas.data ?? []).map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="campo campo-corto">
                <span className="etiqueta">Comuna</span>
                <input
                  aria-label="Comuna"
                  value={f.comuna ?? ''}
                  onChange={(e) => cambiar({ comuna: e.target.value || undefined })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Vendedor</span>
                <select
                  aria-label="Vendedor"
                  value={f.vendedorId ?? ''}
                  onChange={(e) => cambiar({ vendedorId: e.target.value || undefined })}
                >
                  <option value="">Todos</option>
                  {(vendedores.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="campo campo-corto">
                <span className="etiqueta">Producto</span>
                <input
                  aria-label="Producto"
                  placeholder="código o nombre + Enter"
                  value={productoTexto}
                  onChange={(e) => {
                    setProductoTexto(e.target.value);
                    if (!e.target.value.trim()) cambiar({ producto: undefined });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      cambiar({
                        producto:
                          productoTexto.trim().length >= 2 ? productoTexto.trim() : undefined,
                      });
                  }}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Condición</span>
                <select
                  aria-label="Condición de pago"
                  value={f.condicionPago ?? ''}
                  onChange={(e) => cambiar({ condicionPago: e.target.value || undefined })}
                >
                  <option value="">Todas</option>
                  {Object.entries(NOMBRE_CONDICION).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Tarjeta>
          <div className="od-plan-datos">
            <Tarjeta titulo="Datos de la ODD" etiqueta="Datos de la ODD">
              <div className="campos-od">
                <label className="campo">
                  <span className="etiqueta">Nombre de la ODD (opcional)</span>
                  <input
                    aria-label="Nombre de la ODD"
                    placeholder="Ej.: Ruta costa martes"
                    maxLength={80}
                    value={nombreOd}
                    onChange={(e) => setNombreOd(e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Fecha ODD</span>
                  <input
                    type="date"
                    aria-label="Fecha de la ODD"
                    value={fechaOd}
                    onChange={(e) => setFechaOd(e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Camión</span>
                  <select
                    aria-label="Camión"
                    value={vehiculoId}
                    onChange={(e) => setVehiculo(e.target.value)}
                  >
                    <option value="">Sin asignar</option>
                    {(vehiculos.data ?? [])
                      .filter((v) => v.activo)
                      .map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.patente} · {v.descripcion}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="campo">
                  <span className="etiqueta">Chofer · vehículo y carga</span>
                  <select
                    aria-label="Chofer"
                    value={choferId}
                    onChange={(e) => setChofer(e.target.value)}
                  >
                    <option value="">Sin asignar</option>
                    {choferesActivos
                      .filter((c) => (c.rol ?? 'CHOFER') !== 'PEONETA_RECAUDADOR')
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="campo">
                  <span className="etiqueta">Peoneta recaudador · ruta y cobranza</span>
                  <select
                    aria-label="Peoneta recaudador"
                    value={recaudadorId}
                    onChange={(e) => setRecaudador(e.target.value)}
                  >
                    <option value="">Sin asignar</option>
                    {choferesActivos
                      .filter((c) => c.rol === 'PEONETA_RECAUDADOR' || c.rol === 'AMBOS')
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="campo">
                  <span className="etiqueta">Ruta</span>
                  <select
                    aria-label="Ruta"
                    value={rutaId}
                    onChange={(e) => setRuta(e.target.value)}
                  >
                    <option value="">Sin ruta</option>
                    {(rutas.data ?? [])
                      .filter((r) => r.activo)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.codigo} · {r.nombre}
                        </option>
                      ))}
                  </select>
                </label>
                <p className="ayuda">
                  Camiones, choferes, peonetas y rutas se crean en <b>Configuración › Reparto</b>{' '}
                  (Camiones, Choferes y peonetas, Rutas).
                </p>
              </div>
            </Tarjeta>
          </div>
          {q.isError && <PanelError error={q.error} />}
          <Tarjeta
            titulo="Notas de venta por despachar"
            etiqueta="Notas disponibles"
            extra={
              <>
                {elegidas.length} de {filas.length} elegidas
                {filas.length > 0 && (
                  <button
                    type="button"
                    className="boton-mini"
                    onClick={() =>
                      setSel(
                        elegidas.length === filas.length
                          ? new Set()
                          : new Set(filas.map((n) => n.notaId)),
                      )
                    }
                  >
                    {elegidas.length === filas.length ? 'Quitar todas' : 'Elegir todas'}
                  </button>
                )}
              </>
            }
          >
            <Grilla
              etiqueta="Notas de venta disponibles para despacho"
              columnas={columnas}
              filas={filas}
              clave={(n) => n.notaId}
              cargando={q.isLoading}
              vacio="No hay notas confirmadas con despacho pendiente en esta bodega"
              alAbrir={(n) => alternar(n.notaId)}
            />
          </Tarjeta>
          <div className="od-carga" role="group" aria-label="Crear orden de despacho">
            <Tarjeta titulo="Carga" etiqueta="Resumen de la carga">
              <div className="od-carga-fila">
                <DatosDoc
                  datos={[
                    { e: 'Notas', v: String(elegidas.length) },
                    { e: 'Unidades', v: decimal(String(unidades)) },
                    {
                      e: 'Peso',
                      v: (
                        <>
                          {kg(String(kilos))} kg
                          {sinPeso > 0 && (
                            <span className="apagado" title="Productos sin peso en el catálogo">
                              {' '}
                              (* {sinPeso} sin peso)
                            </span>
                          )}
                        </>
                      ),
                    },
                    capacidad !== null &&
                      ocupacion !== null && {
                        e: 'Ocupación',
                        v: (
                          <span
                            className={`ocupacion-camion ${ocupacion > 100 ? 'tono-malo' : ocupacion >= 90 ? 'tono-aviso' : ''}`}
                            data-ocupacion={ocupacion}
                            role="status"
                          >
                            Ocupación {kg(String(kilos))} / {kg(String(capacidad))} kg ({ocupacion}{' '}
                            %)
                            {ocupacion > 100 && ' · se pasa de la capacidad del camión'}
                          </span>
                        ),
                      },
                    { e: 'Monto', v: pesos(String(Math.round(monto))), fuerte: true },
                  ]}
                />
                <div className="od-plan-boton">{boton}</div>
              </div>
            </Tarjeta>
          </div>
        </div>
      </div>
      {detalle && (
        <DetalleNotaPlan
          nota={detalle}
          elegida={sel.has(detalle.notaId)}
          alAlternar={() => alternar(detalle.notaId)}
          alAbrirNota={alAbrirNota}
          alCerrar={() => setDetalle(null)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha de la OD

type Seccion = 'notas' | 'preparacion' | 'manifiesto' | 'documentos' | 'trazabilidad';

/** El faltante descontado, tal como quedó en la bitácora de la OD (paso FALTANTE). */
interface FaltanteDescontado {
  nota: string;
  sku: string;
  pedido: string;
  despachado: string;
  faltante: string;
  registradoEn: string;
}

/**
 * La ficha de la OD. Camino SIMPLE (pedido de Rodrigo, 2026-10-13): la OD recién creada imprime su
 * manifiesto y, mientras el encargado carga, el administrativo descuenta los faltantes (si los hay),
 * emite los documentos e imprime documentos y hoja de rendición; luego el camión sale (ahí se mueve el
 * stock), se rinde y se cierra. Las OD que iban por el camino largo (preparación, validación,
 * aprobación) lo terminan con sus botones de siempre.
 */
function FichaOd({
  odId,
  alAbrirNota,
}: {
  odId: string;
  alAbrirNota?: (notaId: string, numero: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const q = useOd(empresaId, odId);
  const od = q.data;
  const accion = useAccionOd(empresaId, odId);
  const bodegas = useBodegas(empresaId);
  const vendedores = useVendedores(empresaId);
  const [seccion, setSeccion] = useState<Seccion>('notas');
  const [imprimir, setImprimir] = useState<'manifiesto' | 'rendicion' | 'cobranza' | null>(null);
  const [confirmar, setConfirmar] = useState<{
    ruta: string;
    titulo: string;
    texto: string;
    motivo?: boolean;
  } | null>(null);
  const [entregaDe, setEntregaDe] = useState<NotaOd | null>(null);
  const [cobroDe, setCobroDe] = useState<NotaOd | null>(null);
  const [faltanteDe, setFaltanteDe] = useState<string | null>(null);
  const manifiesto = useManifiesto(
    empresaId,
    odId,
    seccion === 'manifiesto' || imprimir === 'manifiesto' || faltanteDe !== null,
  );
  const hayDocumentos = !!od?.notas.some((n) => n.documentacionId);
  const documentos = useDocumentosOd(
    empresaId,
    odId,
    seccion === 'documentos' || imprimir === 'rendicion' || hayDocumentos,
  );
  // El motor emite en segundo plano: mientras falte un folio, la lista se refresca sola.
  const sinFolio = (documentos.data ?? []).some((n) => n.documentos.some((d) => !d.folio));
  useEffect(() => {
    if (!sinFolio) return;
    const t = setInterval(() => void documentos.refetch(), 4000);
    return () => clearInterval(t);
  }, [sinFolio, documentos]);
  const [pdf, setPdf] = useState<{ cargando: boolean; error: unknown }>({
    cargando: false,
    error: null,
  });
  const bodega = bodegas.lista.find((b) => b.id === od?.bodegaId);
  const nombreBodega = bodega ? `${bodega.codigo} · ${bodega.nombre}` : '—';
  const nombreVendedor = useCallback(
    (id: string | null) =>
      id ? ((vendedores.data ?? []).find((v) => v.id === id)?.nombre ?? '—') : '—',
    [vendedores.data],
  );
  const terminarImpresion = useCallback(() => setImprimir(null), []);
  const [conLogistico, setConLogistico] = useState(false);
  if (!od)
    return (
      <div className="pantalla">{q.isError ? <PanelError error={q.error} /> : 'Cargando…'}</div>
    );
  const e = od.estado;
  const largo =
    CAMINO_LARGO.includes(e) || od.historia.some((h) => CAMINO_LARGO.includes(h.hasta as EstadoOd));
  const pasos = largo ? PASOS_OD : PASOS_OD_SIMPLE;
  const ejecutar = (ruta: string, cuerpo: Record<string, unknown> = {}) =>
    accion.mutate({ ruta, cuerpo: { version: od.version, ...cuerpo } });
  const activas = od.notas.filter((n) => n.estado !== 'RETIRADA');
  const sinDocumento = activas.filter((n) => !n.documentacionId);
  // Se emite con todo asignado: patente y chofer salen en la factura.
  const faltan = [
    !od.vehiculoId && 'camión',
    !od.choferId && 'chofer',
    !od.recaudadorId && 'peoneta recaudador',
  ].filter(Boolean);
  const faltaPersonal = faltan.length ? `Antes de emitir asigna: ${faltan.join(', ')}` : undefined;
  const documentoIds = (documentos.data ?? []).flatMap((n) =>
    n.documentos.flatMap((d) => (d.documentoId && d.folio ? [d.documentoId] : [])),
  );
  const faltantes: FaltanteDescontado[] = od.pasos
    .filter((p) => p.paso === 'FALTANTE' && p.estado === 'OK')
    .map((p) => ({
      ...(p.detalle as Omit<FaltanteDescontado, 'registradoEn'>),
      registradoEn: p.registradoEn,
    }));
  const puedeArmar = puede('logistica.od.crear');
  const imprimirDocumentos = async () => {
    setPdf({ cargando: true, error: null });
    try {
      const b = await pedirArchivo('POST', '/dte/documentos/lote-pdf', {
        cuerpo: { empresaId, documentoIds, ejemplar: 'dos', unido: true },
      });
      // Directo a la impresora (Rodrigo, 2026-10-25): cada factura con su original y su cedible; la
      // boleta, que no lleva cedible, sale en dos originales (2026-10-26).
      imprimirPdf(b);
      setPdf({ cargando: false, error: null });
      // Con clientes que pagan al recibir, la hoja de cobranza sale sola con los documentos.
      if (od && clientesContraEntrega(od).length) setImprimir('cobranza');
    } catch (err) {
      setPdf({ cargando: false, error: err });
    }
  };
  // Ver el DTE en otra pestaña, como antes del 25-10 (Rodrigo, 2026-10-26). La pestaña se abre al
  // hacer clic, antes de pedir el PDF, para que el navegador no la bloquee como ventana emergente.
  const verPdf = (documentoId: string) => {
    const ventana = window.open('', '_blank');
    pedirArchivo('GET', `/dte/documentos/${documentoId}/pdf`, { query: { ejemplar: 'ambos' } })
      .then((b) => {
        const url = URL.createObjectURL(b);
        if (ventana) ventana.location.href = url;
        else window.open(url, '_blank', 'noopener');
      })
      .catch((err: unknown) => {
        ventana?.close();
        setPdf({ cargando: false, error: err });
      });
  };
  const pdfsPorNota = new Map(
    (documentos.data ?? []).map((n) => [
      n.notaId,
      n.documentos.flatMap((d) =>
        d.documentoId && d.folio
          ? [{ documentoId: d.documentoId, folio: d.folio, tipoDte: d.tipoDte }]
          : [],
      ),
    ]),
  );
  const botones: Array<{
    etiqueta: string;
    visible: boolean;
    alClick: () => void;
    primario?: boolean;
    deshabilitado?: string | undefined;
  }> = [
    // Camino simple.
    {
      etiqueta: 'Descontar faltante',
      visible: !largo && e === 'PLANIFICADA' && puedeArmar && sinDocumento.length > 0,
      alClick: () => setFaltanteDe(''),
    },
    {
      etiqueta: e === 'PLANIFICADA' ? 'Emitir documentos' : 'Reintentar documentos',
      visible:
        !largo &&
        (e === 'PLANIFICADA' || (e === 'FACTURADA' && sinDocumento.length > 0)) &&
        puede('logistica.od.facturar'),
      alClick: () =>
        setConfirmar({
          ruta: 'facturar',
          titulo: 'Emitir los documentos de la ODD',
          texto: `Se emite la factura o boleta de ${sinDocumento.length} nota(s), por lo que va en el manifiesto, y la mercadería sale de la bodega al camión ${od.patente ?? ''}: la ODD queda en ruta. Si falta algún producto, descuéntalo ANTES: después solo se corrige con nota de crédito.`,
        }),
      primario: true,
      deshabilitado: faltaPersonal,
    },
    {
      etiqueta: 'Imprimir documentos',
      visible: hayDocumentos,
      alClick: () => void imprimirDocumentos(),
      deshabilitado: pdf.cargando
        ? 'Preparando el PDF…'
        : documentoIds.length
          ? undefined
          : 'Los documentos todavía no tienen folio',
    },
    {
      etiqueta: 'Despachar (sale el camión)',
      visible: e === 'FACTURADA' && sinDocumento.length === 0 && puede('logistica.od.salir'),
      alClick: () =>
        setConfirmar({
          ruta: 'salir',
          titulo: 'Despachar la ODD',
          texto: od.trasladoCargaId
            ? 'La ODD sale a ruta.'
            : `La mercadería pasa de la bodega al camión ${od.patente ?? ''} (se descuenta del stock de la bodega) y la ODD sale a ruta.`,
        }),
      primario: true,
      deshabilitado: !od.choferId
        ? 'Asigna el chofer'
        : !od.vehiculoId
          ? 'Asigna el camión'
          : undefined,
    },
    // Camino largo (OD que ya lo habían empezado).
    {
      etiqueta: 'Validar carga',
      visible: e === 'EN_PREPARACION' && puede('logistica.od.validar'),
      alClick: () => ejecutar('validar'),
      primario: true,
    },
    {
      etiqueta: 'Cargar camión',
      visible: e === 'VALIDADA' && puede('logistica.od.cargar'),
      alClick: () =>
        setConfirmar({
          ruta: 'cargar',
          titulo: 'Cargar el camión',
          texto: `Se trasladan ${activas.length} nota(s) de la bodega al camión ${od.patente ?? ''} con sus reservas. Después de cargar ya no se cambian las notas.`,
        }),
      primario: true,
    },
    {
      etiqueta: 'Aprobar para facturación',
      visible: e === 'CARGADA' && puede('logistica.od.aprobar'),
      alClick: () => ejecutar('aprobar'),
      primario: true,
    },
    {
      etiqueta: 'Facturar carga',
      visible: e === 'APROBADA' && puede('logistica.od.facturar'),
      alClick: () =>
        setConfirmar({
          ruta: 'facturar',
          titulo: 'Facturar la carga',
          texto: `Se emite un documento tributario por cada nota cargada (${activas.filter((n) => n.estado === 'CARGADA').length}).`,
        }),
      primario: true,
      deshabilitado: faltaPersonal,
    },
    // Ruta, rendición y cierre (los dos caminos).
    {
      etiqueta: 'Rendir',
      visible: e === 'EN_RUTA' && puede('logistica.od.rendir'),
      alClick: () =>
        setConfirmar({
          ruta: 'rendir',
          titulo: 'Rendir la ODD',
          texto:
            'Las paradas sin registrar se rinden ENTREGADAS COMPLETAS. Si hubo rechazos o entregas parciales, regístralos antes en «Notas y paradas»; los cobros también. Lo rechazado vuelve del camión a la bodega.',
        }),
      primario: true,
    },
    {
      etiqueta: 'Cerrar ODD',
      visible: e === 'RENDIDA' && puede('logistica.od.cerrar'),
      alClick: () =>
        setConfirmar({
          ruta: 'cerrar',
          titulo: 'Cerrar la ODD',
          texto: 'La ODD queda cerrada.',
          motivo: true,
        }),
      primario: true,
    },
    {
      etiqueta: 'Anular',
      visible:
        ['PLANIFICADA', 'EN_PREPARACION', 'VALIDADA'].includes(e) &&
        !hayDocumentos &&
        puede('logistica.od.anular'),
      alClick: () =>
        setConfirmar({
          ruta: 'anular',
          titulo: 'Anular la ODD',
          texto: 'Las notas quedan libres para otra ODD. Nada se movió todavía.',
          motivo: true,
        }),
    },
  ];
  const secciones: Array<[Seccion, string]> = [
    ['notas', 'Notas y paradas'],
    ...(largo ? [['preparacion', 'Preparación'] as [Seccion, string]] : []),
    ['manifiesto', `Manifiesto${faltantes.length ? ` · ${faltantes.length} faltante(s)` : ''}`],
    ['documentos', 'Documentos'],
    ['trazabilidad', 'Trazabilidad'],
  ];
  return (
    <div className="pantalla orden-despacho" data-pantalla="od" data-estado={e}>
      <header className="encabezado-entidad">
        <span className="ruta-migas">Operaciones › Orden de Despacho</span>
        <h2>
          ODD N° {od.numero}{' '}
          <span className={`insignia ${e === 'ANULADA' ? 'tono-malo' : 'tono-neutro'}`}>
            {NOMBRE_ESTADO_OD[e]}
          </span>
        </h2>
        <span className="espaciador" />
        <button type="button" onClick={() => setImprimir('manifiesto')}>
          Imprimir manifiesto de carga
        </button>
        <button type="button" onClick={() => setImprimir('rendicion')}>
          Imprimir hoja de rendición
        </button>
        {clientesContraEntrega(od).length > 0 && (
          <button
            type="button"
            onClick={() => setImprimir('cobranza')}
            title="Cartola de lo pendiente de los clientes que pagan al recibir"
          >
            Imprimir hoja de cobranza ({clientesContraEntrega(od).length})
          </button>
        )}
      </header>
      <ol className="pasos-od" aria-label="Avance de la ODD">
        {pasos.map((p) => {
          const i = pasos.indexOf(p);
          const actual = pasos.indexOf(e);
          return (
            <li
              key={p}
              className={i < actual ? 'hecho' : i === actual ? 'actual' : ''}
              aria-current={i === actual ? 'step' : undefined}
            >
              {NOMBRE_ESTADO_OD[p]}
            </li>
          );
        })}
      </ol>
      <CabeceraOd od={od} bodega={nombreBodega} />
      {!largo && e === 'PLANIFICADA' && (
        <p className="ayuda-od">
          1 · Imprime el manifiesto y entrégalo al encargado de carga. 2 · Si falta algo, «Descontar
          faltante» (eliges de qué notas sale). 3 · «Emitir documentos» e imprime documentos y hoja
          de rendición. 4 · Con chofer y camión, «Despachar».
        </p>
      )}
      <div className="acciones-od" role="toolbar" aria-label="Acciones de la ODD">
        {botones
          .filter((b) => b.visible)
          .map((b) => (
            <button
              key={b.etiqueta}
              type="button"
              className={b.primario ? 'primario' : ''}
              disabled={accion.isPending || !!b.deshabilitado}
              title={b.deshabilitado}
              onClick={b.alClick}
            >
              {b.etiqueta}
            </button>
          ))}
      </div>
      {accion.isError && !confirmar && <PanelError error={accion.error} />}
      {!!pdf.error && <PanelError error={pdf.error} />}
      <div className="secciones-od" role="tablist" aria-label="Secciones de la ODD">
        {secciones.map(([id, n]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={seccion === id}
            className={seccion === id ? 'activa' : ''}
            onClick={() => setSeccion(id)}
          >
            {n}
          </button>
        ))}
      </div>
      {seccion === 'notas' && (
        <NotasDeLaOd
          od={od}
          nombreVendedor={nombreVendedor}
          alAccion={ejecutar}
          ocupado={accion.isPending}
          alEntregar={setEntregaDe}
          alCobrar={setCobroDe}
          pdfsPorNota={pdfsPorNota}
          alVerPdf={verPdf}
          {...(alAbrirNota ? { alAbrirNota } : {})}
        />
      )}
      {seccion === 'preparacion' && (
        <Preparacion od={od} alAccion={ejecutar} ocupado={accion.isPending} />
      )}
      {seccion === 'manifiesto' && (
        <section aria-label="Manifiesto de carga">
          {manifiesto.isError && <PanelError error={manifiesto.error} />}
          <TablaManifiesto
            productos={manifiesto.data?.productos ?? []}
            {...(!largo && e === 'PLANIFICADA' && puedeArmar
              ? { alFaltante: (productoId: string) => setFaltanteDe(productoId) }
              : {})}
          />
          {faltantes.length > 0 && (
            <table className="grilla compacta" aria-label="Faltantes descontados">
              <caption>Faltantes descontados de la carga</caption>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>NV</th>
                  <th>SKU</th>
                  <th className="der">Pedido</th>
                  <th className="der">Despachado</th>
                  <th className="der">Faltante</th>
                </tr>
              </thead>
              <tbody>
                {faltantes.map((f, i) => (
                  <tr key={i}>
                    <td>{fechaHora(f.registradoEn)}</td>
                    <td className="mono">{f.nota}</td>
                    <td className="mono">{f.sku}</td>
                    <td className="der">{decimal(f.pedido)}</td>
                    <td className="der">{decimal(f.despachado)}</td>
                    <td className="der tono-malo">{decimal(f.faltante)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
      {seccion === 'documentos' && (
        <section aria-label="Documentos de la ODD">
          <p className="apagado">
            Una factura o boleta por nota, emitida por el motor DTE propio.
            {sinFolio ? ' Asignando folios…' : ''}
          </p>
          {documentos.isError && <PanelError error={documentos.error} />}
          <table className="grilla compacta" aria-label="Documentos de la carga">
            <thead>
              <tr>
                <th>NV</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Folio</th>
                <th>Estado</th>
                <th className="der">Monto</th>
                <th>Incidencia</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(documentos.data ?? []).flatMap((n) =>
                (n.documentos.length ? n.documentos : [null]).map((d, i) => (
                  <tr key={`${n.notaId}-${i}`}>
                    <td className="mono">{n.numeroVisible}</td>
                    <td>{n.cliente}</td>
                    <td>
                      {d?.tipoDte
                        ? (NOMBRE_TIPO_DTE[d.tipoDte] ?? d.tipoDte)
                        : n.documentoSolicitado === 'FACTURA'
                          ? 'Factura (por emitir)'
                          : 'Boleta (por emitir)'}
                    </td>
                    <td className="mono">{d?.folio ?? '—'}</td>
                    <td>{d?.estado ?? 'Sin documento'}</td>
                    <td className="der">{d?.monto ? pesos(d.monto) : '—'}</td>
                    <td className="tono-malo">{n.incidencia ?? ''}</td>
                    <td className="acciones-fila">
                      {d?.documentoId && d.folio && (
                        <>
                          <button
                            type="button"
                            className="boton-mini"
                            title="Abre el PDF en otra pestaña"
                            onClick={() => verPdf(d.documentoId!)}
                          >
                            Ver
                          </button>
                          <button
                            type="button"
                            className="boton-mini"
                            title="Directo a la impresora (boleta en dos copias, factura con cedible)"
                            onClick={() =>
                              void pedirArchivo('GET', `/dte/documentos/${d.documentoId}/pdf`, {
                                query: { ejemplar: 'dos' },
                              })
                                .then(imprimirPdf)
                                .catch((err: unknown) => setPdf({ cargando: false, error: err }))
                            }
                          >
                            Imprimir
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </section>
      )}
      {seccion === 'trazabilidad' && <Trazabilidad od={od} />}
      {confirmar && (
        <ConfirmarAccion
          {...confirmar}
          extra={
            confirmar.ruta === 'facturar' && !largo ? (
              <label className={`costo-logistico-od${conLogistico ? ' activo' : ''}`}>
                <input
                  type="checkbox"
                  aria-label="Aplicar costo logístico"
                  checked={conLogistico}
                  onChange={(ev) => setConLogistico(ev.target.checked)}
                />
                <span className="costo-logistico-od-texto">
                  <b>Desglose logístico en las facturas</b>
                  <span className="costo-logistico-od-estado">
                    {conLogistico
                      ? 'Las facturas de esta orden separan la logística en una línea LOGÍSTICA.'
                      : 'Sin desglose: las facturas salen con el precio completo en cada producto.'}
                  </span>
                  <ul className="ayuda">
                    <li>
                      El monto por producto (con IVA) se configura en{' '}
                      <b>Configuración › Costo logístico por producto</b>.
                    </li>
                    <li>
                      <b>El total no cambia</b>: la logística lleva solo IVA, así que baja el
                      impuesto específico (ILA / IABA).
                    </li>
                    <li>Impresa: precios de lista en el detalle y «Logistica» al pie.</li>
                    <li>Solo facturas: las boletas no llevan desglose.</li>
                  </ul>
                </span>
              </label>
            ) : undefined
          }
          ocupado={accion.isPending}
          error={accion.isError ? accion.error : null}
          alCerrar={() => {
            accion.reset();
            setConfirmar(null);
          }}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: confirmar.ruta,
                cuerpo: {
                  version: od.version,
                  ...(motivo ? { motivo } : {}),
                  ...(confirmar.ruta === 'facturar' && conLogistico
                    ? { costoLogistico: true }
                    : {}),
                },
              },
              {
                onSuccess: () => {
                  if (confirmar.ruta === 'facturar') setSeccion('documentos');
                  setConfirmar(null);
                },
              },
            )
          }
        />
      )}
      {faltanteDe !== null && (
        <DialogoFaltante
          od={od}
          productos={manifiesto.data?.productos ?? []}
          productoInicial={faltanteDe}
          alCerrar={() => setFaltanteDe(null)}
        />
      )}
      {entregaDe && (
        <DialogoEntrega
          empresaId={empresaId}
          odId={odId}
          nota={entregaDe}
          alCerrar={() => setEntregaDe(null)}
        />
      )}
      {cobroDe && (
        <DialogoCobro
          empresaId={empresaId}
          odId={odId}
          nota={cobroDe}
          registrable={
            ['EN_RUTA', 'RENDIDA'].includes(od?.estado ?? '') && puede('logistica.od.cobrar')
          }
          alCerrar={() => setCobroDe(null)}
        />
      )}
      {imprimir === 'manifiesto' && manifiesto.data && (
        <Imprimir alTerminar={terminarImpresion}>
          <ManifiestoImpreso
            od={od}
            productos={manifiesto.data.productos}
            resumen={manifiesto.data.resumen}
            empresa={{ razonSocial: empresa!.razon_social, rut: empresa!.rut, dv: empresa!.dv }}
            bodega={bodega?.codigo ?? ''}
          />
        </Imprimir>
      )}
      {imprimir === 'cobranza' && (
        <Imprimir alTerminar={terminarImpresion}>
          <HojaCobranzaImpresa empresaId={empresaId} empresa={empresa!.razon_social} od={od} />
        </Imprimir>
      )}
      {imprimir === 'rendicion' && documentos.data && (
        <Imprimir horizontal alTerminar={terminarImpresion}>
          <HojaRendicionImpresa
            od={od}
            documentos={documentos.data}
            empresa={{ razonSocial: empresa!.razon_social, rut: empresa!.rut, dv: empresa!.dv }}
            bodega={bodega?.codigo ?? ''}
          />
        </Imprimir>
      )}
    </div>
  );
}

/**
 * Descontar un faltante (pedido de Rodrigo, 2026-10-13): se elige el producto y, por cada nota que lo
 * lleva, cuánto se despacha. El faltante se puede repartir entre varias notas o cargar entero a una:
 * criterio de quien arma la OD. Cada nota rebajada queda con su revisión nueva antes de documentar, y
 * lo que faltó queda pendiente en una nota de saldo del mismo cliente (vuelve a «Nueva OD»).
 */
interface Saldo {
  notaId: string;
  numeroVisible: string;
  estado: string;
  de: string;
}

function DialogoFaltante({
  od,
  productos,
  productoInicial,
  alCerrar,
}: {
  od: Od;
  productos: ProductoManifiesto[];
  productoInicial: string;
  alCerrar: () => void;
}) {
  const accion = useAccionOd(useSesion().empresa!.id, od.id);
  const [productoId, setProductoId] = useState(productoInicial);
  const [buscar, setBuscar] = useState('');
  const [despachar, setDespachar] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const [saldos, setSaldos] = useState<Saldo[] | null>(null);
  const producto = productos.find((p) => p.productoId === productoId);
  const notas = useMemo(
    () =>
      producto
        ? producto.detalle.filter((d) => {
            const n = od.notas.find((x) => x.notaId === d.notaId);
            return n && !n.documentacionId;
          })
        : [],
    [producto, od.notas],
  );
  const valor = (notaId: string, pedido: string) => despachar[notaId] ?? decimal(pedido);
  const numero = (s: string) => Number(s.replace(/\./g, '').replace(',', '.'));
  const totalPedido = notas.reduce((a, d) => a + Number(d.cantidad), 0);
  const totalDespacho = notas.reduce((a, d) => a + numero(valor(d.notaId, d.cantidad)), 0);
  const faltante = totalPedido - totalDespacho;
  const invalida = notas.some((d) => {
    const v = numero(valor(d.notaId, d.cantidad));
    return !Number.isFinite(v) || v < 0 || v > Number(d.cantidad);
  });
  const filtrados = productos.filter((p) => coincide(buscar, p.sku, p.descripcion));
  const guardar = () =>
    accion.mutate(
      {
        ruta: 'faltante',
        cuerpo: {
          version: od.version,
          productoId,
          despachar: notas.map((d) => ({
            notaId: d.notaId,
            cantidad: String(numero(valor(d.notaId, d.cantidad))),
          })),
          ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
        },
      },
      {
        onSuccess: (r) => {
          const s = (r as { saldos?: Saldo[] }).saldos ?? [];
          if (s.length) setSaldos(s);
          else alCerrar();
        },
      },
    );
  if (saldos)
    return (
      <Modal titulo={`Faltante descontado · ODD N° ${od.numero}`} alCerrar={alCerrar}>
        <p>Lo que faltó no se pierde: quedó pendiente de despacho en una nota de saldo.</p>
        <ul aria-label="Notas de saldo">
          {saldos.map((x) => (
            <li key={x.notaId}>
              <span className="mono">{x.de}</span> → saldo en{' '}
              <b className="mono">{x.numeroVisible}</b>
              {x.estado === 'CONFIRMADA'
                ? ' · aparece en «Nueva ODD» para el próximo despacho'
                : ' · espera aprobación comercial antes de despacharse'}
            </li>
          ))}
        </ul>
        <div className="acciones-modal">
          <button type="button" className="primario" autoFocus onClick={alCerrar}>
            Listo
          </button>
        </div>
      </Modal>
    );
  return (
    <Modal titulo={`Descontar faltante · ODD N° ${od.numero}`} alCerrar={alCerrar}>
      {!producto ? (
        <>
          <p>¿Qué producto falta?</p>
          <input
            aria-label="Buscar producto"
            placeholder="SKU o descripción"
            autoFocus
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
          <table className="grilla compacta" aria-label="Productos de la carga">
            <tbody>
              {filtrados.map((p) => (
                <tr key={p.productoId}>
                  <td className="mono">{p.sku}</td>
                  <td>{p.descripcion}</td>
                  <td className="der">{decimal(p.total)}</td>
                  <td>
                    <button
                      type="button"
                      className="boton-mini"
                      onClick={() => setProductoId(p.productoId)}
                    >
                      Elegir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <>
          <p>
            <b className="mono">{producto.sku}</b> · {producto.descripcion}. Escribe cuánto se
            despacha a cada nota (en unidades). Lo que no se despacha sale de la nota y queda
            pendiente en una nota de saldo del mismo cliente, para otro despacho.
          </p>
          <table className="grilla compacta" aria-label="Despacho por nota">
            <thead>
              <tr>
                <th>Parada</th>
                <th>NV</th>
                <th>Cliente</th>
                <th className="der">Pedido</th>
                <th className="der">A despachar</th>
              </tr>
            </thead>
            <tbody>
              {notas.map((d) => (
                <tr key={d.notaId}>
                  <td>{d.parada}</td>
                  <td className="mono">{d.numeroVisible}</td>
                  <td>{d.cliente}</td>
                  <td className="der">{decimal(d.cantidad)}</td>
                  <td className="der">
                    <input
                      className="mono entrada-corta"
                      inputMode="decimal"
                      aria-label={`Despachar ${d.numeroVisible}`}
                      value={valor(d.notaId, d.cantidad)}
                      onChange={(e) => setDespachar({ ...despachar, [d.notaId]: e.target.value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p role="status" className={faltante > 0 ? 'tono-aviso' : 'apagado'}>
            Faltante: {decimal(String(Math.max(0, faltante)))} de {decimal(String(totalPedido))}
          </p>
          <label className="campo">
            <span className="etiqueta">Motivo (opcional)</span>
            <input
              aria-label="Motivo del faltante"
              placeholder={`Faltante en la carga de la ODD N° ${od.numero}`}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        </>
      )}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button
          type="button"
          onClick={producto && !productoInicial ? () => setProductoId('') : alCerrar}
        >
          Volver
        </button>
        {producto && (
          <button
            type="button"
            className="primario"
            disabled={accion.isPending || invalida || faltante <= 0}
            onClick={guardar}
          >
            Descontar {decimal(String(Math.max(0, faltante)))}
          </button>
        )}
      </div>
    </Modal>
  );
}

function CabeceraOd({ od, bodega }: { od: Od; bodega: string }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const vehiculos = useVehiculos(empresaId);
  const choferes = useChoferes(empresaId);
  const rutas = useRutas(empresaId);
  const editable =
    ['PLANIFICADA', 'EN_PREPARACION', 'VALIDADA'].includes(od.estado) &&
    puede('logistica.od.crear');
  const asignar = useMutation({
    mutationFn: (cambio: Record<string, unknown>) =>
      api.patch(`/logistica/ordenes/${od.id}`, { empresaId, version: od.version, ...cambio }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesLog.logistica(empresaId) }),
  });
  const campo = (
    etiqueta: string,
    valor: string | null,
    opciones: Array<{ id: string; texto: string }>,
    clave: string,
    actual: string | null,
  ) =>
    editable ? (
      <select
        aria-label={etiqueta}
        value={actual ?? ''}
        disabled={asignar.isPending}
        onChange={(ev) => asignar.mutate({ [clave]: ev.target.value || null })}
      >
        <option value="">Sin asignar</option>
        {opciones.map((o) => (
          <option key={o.id} value={o.id}>
            {o.texto}
          </option>
        ))}
      </select>
    ) : (
      (valor ?? '—')
    );
  return (
    <>
      <dl className="cabecera-od">
        <div>
          <dt>N° ODD</dt>
          <dd className="mono">{od.numero}</dd>
        </div>
        <div>
          <dt>Nombre</dt>
          <dd>{od.nombre ?? '—'}</dd>
        </div>
        <div>
          <dt>Fecha</dt>
          <dd>{fecha(od.fechaNegocio)}</dd>
        </div>
        <div>
          <dt>Bodega origen</dt>
          <dd>{bodega}</dd>
        </div>
        <div>
          <dt>Camión</dt>
          <dd>
            {campo(
              'Camión',
              od.patente ? `${od.patente} · ${od.vehiculoDescripcion ?? ''}` : null,
              (vehiculos.data ?? [])
                .filter((v) => v.activo)
                .map((v) => ({ id: v.id, texto: `${v.patente} · ${v.descripcion}` })),
              'vehiculoId',
              od.vehiculoId,
            )}
          </dd>
        </div>
        <div>
          <dt>Chofer</dt>
          <dd>
            {campo(
              'Chofer',
              od.chofer,
              (choferes.data ?? [])
                .filter((c) => c.activo && c.rol !== 'PEONETA_RECAUDADOR')
                .map((c) => ({ id: c.id, texto: c.nombre })),
              'choferId',
              od.choferId,
            )}
          </dd>
        </div>
        <div>
          <dt>Peoneta recaudador</dt>
          <dd>
            {campo(
              'Peoneta recaudador',
              od.recaudador ?? null,
              (choferes.data ?? [])
                .filter((c) => c.activo && (c.rol === 'PEONETA_RECAUDADOR' || c.rol === 'AMBOS'))
                .map((c) => ({ id: c.id, texto: c.nombre })),
              'recaudadorId',
              od.recaudadorId ?? null,
            )}
          </dd>
        </div>
        {od.ayudantes.length > 0 && (
          <div>
            <dt>Ayudantes</dt>
            <dd>{od.ayudantes.join(', ')}</dd>
          </div>
        )}
        <div>
          <dt>Ruta</dt>
          <dd>
            {campo(
              'Ruta',
              od.ruta,
              (rutas.data ?? [])
                .filter((r) => r.activo)
                .map((r) => ({ id: r.id, texto: `${r.codigo} · ${r.nombre}` })),
              'rutaId',
              od.rutaId,
            )}
          </dd>
        </div>
        <div>
          <dt>Notas</dt>
          <dd>{od.notas.filter((n) => n.estado !== 'RETIRADA').length}</dd>
        </div>
      </dl>
      {asignar.isError && <PanelError error={asignar.error} />}
    </>
  );
}

function NotasDeLaOd({
  od,
  nombreVendedor,
  alAccion,
  ocupado,
  alEntregar,
  alCobrar,
  pdfsPorNota,
  alVerPdf,
  alAbrirNota,
}: {
  od: Od;
  nombreVendedor: (id: string | null) => string;
  alAccion: (ruta: string, cuerpo?: Record<string, unknown>) => void;
  ocupado: boolean;
  alEntregar: (n: NotaOd) => void;
  alCobrar: (n: NotaOd) => void;
  /** Los DTE con folio de cada nota (para «Ver PDF» en la fila). */
  pdfsPorNota: Map<string, Array<{ documentoId: string; folio: string; tipoDte: number | null }>>;
  alVerPdf: (documentoId: string) => void;
  alAbrirNota?: (notaId: string, numero: string) => void;
}) {
  const { puede } = useSesion();
  const editable =
    ['PLANIFICADA', 'EN_PREPARACION', 'VALIDADA'].includes(od.estado) &&
    puede('logistica.od.crear');
  const enRuta = od.estado === 'EN_RUTA' || od.estado === 'RENDIDA';
  const puedeEntregar = enRuta && puede('logistica.od.entregar');
  const puedeCobrar = enRuta && puede('logistica.od.cobrar');
  const activas = od.notas.filter((n) => n.estado !== 'RETIRADA');
  const mover = (i: number, d: -1 | 1) => {
    const orden = activas.map((n) => n.notaId);
    const j = i + d;
    if (j < 0 || j >= orden.length) return;
    [orden[i], orden[j]] = [orden[j]!, orden[i]!];
    alAccion('paradas', { notaIds: orden });
  };
  const [retirar, setRetirar] = useState<NotaOd | null>(null);
  const [motivo, setMotivo] = useState('');
  return (
    <section aria-label="Notas y paradas">
      <table className="grilla compacta" aria-label="Notas de la ODD">
        <thead>
          <tr>
            <th>Parada</th>
            <th>NV</th>
            <th>Cliente</th>
            <th>Dirección</th>
            <th>Zona</th>
            <th>Condición</th>
            <th>Vendedor</th>
            <th>Estado</th>
            <th>Incidencia</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {od.notas.map((n) => {
            const i = activas.indexOf(n);
            return (
              <tr key={n.notaId} className={n.estado === 'RETIRADA' ? 'apagado' : ''}>
                <td>{n.estado === 'RETIRADA' ? '—' : n.parada}</td>
                <td className="mono">
                  {alAbrirNota ? (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => alAbrirNota(n.notaId, n.numeroVisible)}
                    >
                      {n.numeroVisible}
                    </button>
                  ) : (
                    n.numeroVisible
                  )}
                </td>
                <td>{n.cliente}</td>
                <td className="apagado">{n.direccionEntrega ?? '—'}</td>
                <td>{n.zona ?? '—'}</td>
                <td>
                  {NOMBRE_CONDICION[n.condicionPago] ?? n.condicionPago}
                  {n.cobroContraEntrega && (
                    <span
                      className="marca-contra-entrega"
                      title="El cliente paga lo pendiente al recibir: va la hoja de cobranza"
                    >
                      COBRAR AL ENTREGAR
                    </span>
                  )}
                </td>
                <td>{nombreVendedor(n.vendedorId)}</td>
                <td>
                  {n.estado.replace('_', ' ').toLowerCase()}
                  {n.requiereNc && <span className="insignia tono-aviso"> requiere NC</span>}
                </td>
                <td className="tono-malo">{n.incidencia ?? n.motivo ?? ''}</td>
                <td className="acciones-fila">
                  {n.estado !== 'RETIRADA' &&
                    (pdfsPorNota.get(n.notaId) ?? []).map((p) => (
                      <button
                        key={p.documentoId}
                        type="button"
                        className="boton-mini"
                        title={`${p.tipoDte ? (NOMBRE_TIPO_DTE[p.tipoDte] ?? p.tipoDte) : 'DTE'} N° ${p.folio}: abre el PDF en otra pestaña`}
                        onClick={() => alVerPdf(p.documentoId)}
                      >
                        Ver PDF{(pdfsPorNota.get(n.notaId) ?? []).length > 1 ? ` ${p.folio}` : ''}
                      </button>
                    ))}
                  {editable && n.estado !== 'RETIRADA' && !n.documentacionId && (
                    <>
                      <button
                        type="button"
                        className="boton-mini"
                        aria-label={`Subir ${n.numeroVisible}`}
                        disabled={ocupado || i === 0}
                        onClick={() => mover(i, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="boton-mini"
                        aria-label={`Bajar ${n.numeroVisible}`}
                        disabled={ocupado || i === activas.length - 1}
                        onClick={() => mover(i, 1)}
                      >
                        ↓
                      </button>
                      <button type="button" className="boton-mini" onClick={() => setRetirar(n)}>
                        Retirar
                      </button>
                    </>
                  )}
                  {puedeEntregar &&
                    n.estado !== 'RETIRADA' &&
                    od.estado === 'EN_RUTA' &&
                    n.lineas.every((l) => l.entregado === null) && (
                      <button
                        type="button"
                        className="boton-mini primario"
                        onClick={() => alEntregar(n)}
                      >
                        Registrar entrega
                      </button>
                    )}
                  {(puedeCobrar || n.cobros.length > 0) && n.estado !== 'RETIRADA' && (
                    <button type="button" className="boton-mini" onClick={() => alCobrar(n)}>
                      Cobro{n.cobros.length ? ` (${n.cobros.length})` : ''}
                      {n.cobros.some((c) => !c.anulado && !c.reciboId) ? ' · por abonar' : ''}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {retirar && (
        <Modal
          titulo={`Retirar ${retirar.numeroVisible} de la ODD`}
          alCerrar={() => setRetirar(null)}
        >
          <p>La nota queda en la bodega con su reserva, disponible para otra ODD.</p>
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo del retiro"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="acciones-modal">
            <button type="button" onClick={() => setRetirar(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={motivo.trim().length < 3}
              onClick={() => {
                alAccion('retirar', { notaId: retirar.notaId, motivo: motivo.trim() });
                setRetirar(null);
                setMotivo('');
              }}
            >
              Retirar
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function Preparacion({
  od,
  alAccion,
  ocupado,
}: {
  od: Od;
  alAccion: (ruta: string, cuerpo?: Record<string, unknown>) => void;
  ocupado: boolean;
}) {
  const { empresa, puede } = useSesion();
  const editable =
    ['PLANIFICADA', 'EN_PREPARACION', 'VALIDADA'].includes(od.estado) &&
    puede('logistica.od.preparar');
  const revision = useRevisionCarga(
    empresa!.id,
    od.id,
    od.estado === 'EN_PREPARACION' || od.estado === 'PLANIFICADA',
  );
  const [cambios, setCambios] = useState<Record<string, string>>({});
  const filas = useMemo(
    () =>
      od.notas
        .filter((n) => n.estado !== 'RETIRADA')
        .flatMap((n) => n.lineas.map((l) => ({ n, l }))),
    [od.notas],
  );
  const valor = (l: LineaOd) =>
    cambios[l.notaLineaId] ?? (l.preparado === null ? '' : decimal(l.preparado).replace(/\./g, ''));
  const pendientes = Object.keys(cambios).length;
  const guardar = () =>
    alAccion('preparacion', {
      lineas: Object.entries(cambios).map(([notaLineaId, v]) => ({
        notaLineaId,
        preparado: v.replace(/\./g, '').replace(',', '.') || '0',
      })),
    });
  return (
    <section aria-label="Preparación de la carga">
      <div className="barra-filtros">
        <span className="apagado">
          Registra lo que realmente se preparó por línea. Una nota con diferencias no viaja: se
          completa o se retira de la ODD (no hay facturación parcial de una línea).
        </span>
        <span className="espaciador" />
        {editable && (
          <>
            <button
              type="button"
              onClick={() =>
                setCambios(
                  Object.fromEntries(
                    filas.map(({ l }) => [
                      l.notaLineaId,
                      decimal(l.planificado).replace(/\./g, ''),
                    ]),
                  ),
                )
              }
            >
              Todo conforme
            </button>
            <button
              type="button"
              className="primario"
              disabled={!pendientes || ocupado}
              onClick={() => {
                guardar();
                setCambios({});
              }}
            >
              Guardar preparación{pendientes ? ` (${pendientes})` : ''}
            </button>
          </>
        )}
      </div>
      <table className="grilla compacta" aria-label="Líneas a preparar">
        <thead>
          <tr>
            <th>Parada</th>
            <th>NV</th>
            <th>SKU</th>
            <th>Producto</th>
            <th className="der">Esperado</th>
            <th className="der">Preparado</th>
            <th className="der">Diferencia</th>
            <th className="der">Cargado</th>
            <th className="der">Entregado</th>
          </tr>
        </thead>
        <tbody>
          {filas.map(({ n, l }) => {
            const prep = Number(valor(l).replace(',', '.') || NaN);
            const dif = Number.isNaN(prep) ? null : prep - Number(l.planificado);
            return (
              <tr key={l.notaLineaId}>
                <td>{n.parada}</td>
                <td className="mono">{n.numeroVisible}</td>
                <td className="mono">{l.sku}</td>
                <td>{l.descripcion}</td>
                <td className="der">{decimal(l.planificado)}</td>
                <td className="der">
                  {editable ? (
                    <input
                      className="entrada-numero"
                      inputMode="decimal"
                      aria-label={`Preparado ${n.numeroVisible} ${l.sku}`}
                      value={valor(l)}
                      onChange={(e) =>
                        setCambios((c) => ({ ...c, [l.notaLineaId]: e.target.value }))
                      }
                    />
                  ) : l.preparado === null ? (
                    '—'
                  ) : (
                    decimal(l.preparado)
                  )}
                </td>
                <td className={`der ${dif ? 'tono-malo' : ''}`}>
                  {dif === null ? '' : dif ? decimal(String(dif)) : '✓'}
                </td>
                <td className="der">{l.cargado ? decimal(l.cargado) : '—'}</td>
                <td className="der">{l.entregado ? decimal(l.entregado) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {revision.data && revision.data.length > 0 && <Diferencias problemas={revision.data} />}
    </section>
  );
}

function Diferencias({ problemas }: { problemas: DiferenciaCarga[] }) {
  return (
    <section className="panel-diferencias" aria-label="Diferencias de la carga">
      <h3>CARGA CON DIFERENCIAS · {problemas.length}</h3>
      <table className="grilla compacta">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            <th>NV</th>
            <th>Cliente</th>
            <th className="der">Esperado</th>
            <th className="der">Preparado / disponible</th>
            <th className="der">Faltante</th>
            <th>Problema</th>
          </tr>
        </thead>
        <tbody>
          {problemas.map((p, i) => (
            <tr key={i}>
              <td className="mono">{p.sku ?? ''}</td>
              <td>{p.descripcion ?? ''}</td>
              <td className="mono">{p.numeroVisible ?? ''}</td>
              <td>{p.cliente ?? ''}</td>
              <td className="der">{p.esperado ? decimal(p.esperado) : ''}</td>
              <td className="der">{p.preparado ? decimal(p.preparado) : ''}</td>
              <td className="der tono-malo">{p.faltante ? decimal(p.faltante) : ''}</td>
              <td>{p.problema}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function TablaManifiesto({
  productos,
  alFaltante,
}: {
  alFaltante?: (productoId: string) => void;
  productos: Array<{
    productoId: string;
    sku: string;
    descripcion: string;
    total: string;
    detalle: Array<{
      notaId: string;
      numeroVisible: string;
      cliente: string;
      parada: number;
      cantidad: string;
    }>;
  }>;
}) {
  return (
    <div className="dos-columnas">
      <table className="grilla compacta" aria-label="Consolidado por producto">
        <caption>A · Consolidado por producto</caption>
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            <th className="der">Cantidad</th>
            {alFaltante && <th />}
          </tr>
        </thead>
        <tbody>
          {productos.map((p) => (
            <tr key={p.productoId}>
              <td className="mono">{p.sku}</td>
              <td>{p.descripcion}</td>
              <td className="der">
                <b>{decimal(p.total)}</b>
              </td>
              {alFaltante && (
                <td>
                  <button
                    type="button"
                    className="boton-mini"
                    aria-label={`Faltante de ${p.sku}`}
                    onClick={() => alFaltante(p.productoId)}
                  >
                    Falta…
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <table className="grilla compacta" aria-label="Desglose por nota y cliente">
        <caption>B · Producto → NV → cliente</caption>
        <thead>
          <tr>
            <th>SKU</th>
            <th>Parada</th>
            <th>NV</th>
            <th>Cliente</th>
            <th className="der">Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {productos.flatMap((p) =>
            p.detalle.map((d, i) => (
              <tr key={`${p.productoId}-${d.notaId}`}>
                <td className="mono">{i === 0 ? p.sku : ''}</td>
                <td>{d.parada}</td>
                <td className="mono">{d.numeroVisible}</td>
                <td>{d.cliente}</td>
                <td className="der">{decimal(d.cantidad)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}

function Trazabilidad({ od }: { od: Od }) {
  return (
    <section aria-label="Trazabilidad de la ODD" className="dos-columnas">
      <table className="grilla compacta" aria-label="Historia de estados">
        <caption>Historia</caption>
        <thead>
          <tr>
            <th>Cuándo</th>
            <th>Estado</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {od.historia.map((h, i) => (
            <tr key={i}>
              <td>{fechaHora(h.registradoEn)}</td>
              <td>{NOMBRE_ESTADO_OD[h.hasta as EstadoOd] ?? h.hasta}</td>
              <td className="apagado">{h.motivo ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="grilla compacta" aria-label="Pasos orquestados">
        <caption>Pasos (traslados, documentos, despachos)</caption>
        <thead>
          <tr>
            <th>Cuándo</th>
            <th>Paso</th>
            <th>NV</th>
            <th>Resultado</th>
          </tr>
        </thead>
        <tbody>
          {od.pasos.map((p, i) => (
            <tr key={i} className={p.estado === 'FALLIDO' ? 'tono-malo' : ''}>
              <td>{fechaHora(p.registradoEn)}</td>
              <td>{p.paso.toLowerCase()}</td>
              <td className="mono">
                {od.notas.find((n) => n.notaId === p.notaId)?.numeroVisible ?? ''}
              </td>
              <td className="mono">
                {p.estado === 'OK' ? (p.resultadoId ?? '').slice(0, 8) : p.error}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ConfirmarAccion({
  titulo,
  texto,
  motivo: conMotivo,
  extra,
  ocupado,
  error,
  alCerrar,
  alConfirmar,
}: {
  titulo: string;
  texto: string;
  motivo?: boolean;
  extra?: ReactNode;
  ocupado: boolean;
  error: unknown;
  alCerrar: () => void;
  alConfirmar: (motivo?: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Modal titulo={titulo} alCerrar={alCerrar}>
      <p>{texto}</p>
      {extra}
      {conMotivo && (
        <label className="campo">
          <span className="etiqueta">Motivo</span>
          <input
            aria-label="Motivo"
            autoFocus
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
      )}
      {!!error && <PanelError error={error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          autoFocus={!conMotivo}
          disabled={ocupado || (conMotivo && motivo.trim().length < 3)}
          onClick={() => alConfirmar(conMotivo ? motivo.trim() : undefined)}
        >
          Confirmar
        </button>
      </div>
    </Modal>
  );
}

function DialogoEntrega({
  empresaId,
  odId,
  nota,
  alCerrar,
}: {
  empresaId: string;
  odId: string;
  nota: NotaOd;
  alCerrar: () => void;
}) {
  const accion = useAccionOd(empresaId, odId);
  const [entregado, setEntregado] = useState<Record<string, string>>(
    Object.fromEntries(
      nota.lineas.map((l) => [
        l.notaLineaId,
        decimal(l.cargado ?? l.planificado).replace(/\./g, ''),
      ]),
    ),
  );
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [obs, setObs] = useState('');
  // Lo devuelto de una nota facturada: ¿lo quiere otro día o va nota de crédito? (2026-10-31)
  const [destino, setDestino] = useState<'NC' | 'REENTREGA' | null>(null);
  const cantidadDe = (l: NotaOd['lineas'][number]) =>
    (entregado[l.notaLineaId] ?? '0').replace(/\./g, '').replace(',', '.') || '0';
  const devuelve =
    !!nota.documentacionId &&
    nota.lineas.some((l) => Number(cantidadDe(l)) < Number(l.cargado ?? '0'));
  return (
    <Modal
      titulo={`Entrega · parada ${nota.parada} · ${nota.cliente}`}
      alCerrar={alCerrar}
      ancho="ancho"
    >
      <table className="grilla compacta" aria-label="Entrega por línea">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            <th className="der">Cargado</th>
            <th className="der">Entregado</th>
            <th>Motivo de rechazo</th>
          </tr>
        </thead>
        <tbody>
          {nota.lineas.map((l) => (
            <tr key={l.notaLineaId}>
              <td className="mono">{l.sku}</td>
              <td>{l.descripcion}</td>
              <td className="der">{decimal(l.cargado ?? '0')}</td>
              <td className="der">
                <input
                  className="entrada-numero"
                  inputMode="decimal"
                  aria-label={`Entregado ${l.sku}`}
                  value={entregado[l.notaLineaId] ?? ''}
                  onChange={(e) => setEntregado((x) => ({ ...x, [l.notaLineaId]: e.target.value }))}
                />
              </td>
              <td>
                <input
                  aria-label={`Motivo rechazo ${l.sku}`}
                  value={motivos[l.notaLineaId] ?? ''}
                  onChange={(e) => setMotivos((x) => ({ ...x, [l.notaLineaId]: e.target.value }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="campo">
        <span className="etiqueta">Observaciones</span>
        <input
          aria-label="Observaciones de la entrega"
          value={obs}
          onChange={(e) => setObs(e.target.value)}
        />
      </label>
      {devuelve && (
        <fieldset className="campo" aria-label="Lo devuelto">
          <span className="etiqueta">Lo devuelto, ¿lo quiere otro día?</span>
          <label className="campo-linea">
            <input
              type="radio"
              name="destino-rechazo"
              checked={destino === 'NC'}
              onChange={() => setDestino('NC')}
            />{' '}
            <b>No lo quiere</b>: va nota de crédito
          </label>
          <label className="campo-linea">
            <input
              type="radio"
              name="destino-rechazo"
              checked={destino === 'REENTREGA'}
              onChange={() => setDestino('REENTREGA')}
            />{' '}
            <b>Lo quiere otro día</b>: vuelve a salir con la misma factura
          </label>
        </fieldset>
      )}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={accion.isPending || (devuelve && !destino)}
          onClick={() =>
            accion.mutate(
              {
                ruta: 'entregas',
                cuerpo: {
                  notaId: nota.notaId,
                  lineas: nota.lineas.map((l) => ({
                    notaLineaId: l.notaLineaId,
                    entregado: cantidadDe(l),
                    motivoRechazo: motivos[l.notaLineaId] || null,
                  })),
                  observaciones: obs || null,
                  ...(devuelve && destino ? { destinoRechazo: destino } : {}),
                },
              },
              { onSuccess: alCerrar },
            )
          }
        >
          Registrar entrega
        </button>
      </div>
    </Modal>
  );
}

function DialogoCobro({
  empresaId,
  odId,
  nota,
  registrable,
  alCerrar,
}: {
  empresaId: string;
  odId: string;
  nota: NotaOd;
  /** En ruta o rindiendo se registran cobros (rendición a mano, docs/111); después solo se miran. */
  registrable: boolean;
  alCerrar: () => void;
}) {
  const accion = useAccionOd(empresaId, odId);
  const imputar = useAccionOd(empresaId, odId);
  const anular = useAccionOd(empresaId, odId);
  const { puede } = useSesion();
  const [cheque, setCheque] = useState({ banco: '', numeroDocumento: '', fechaDocumento: '' });
  const [medio, setMedio] = useState<'EFECTIVO' | 'TRANSFERENCIA' | 'CHEQUE'>('EFECTIVO');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const valor = monto.replace(/\D/g, '');
  /** docs/111 · la deuda baja cuando tesorería recibe: aquí solo se ve en qué va cada cobro. */
  const estado = (c: NotaOd['cobros'][number]) =>
    c.anulado
      ? { texto: 'anulado', tono: 'tono-neutro' }
      : c.reciboId
        ? { texto: 'abonado a la cuenta', tono: 'tono-ok' }
        : c.estadoTesoreria === 'POR_VERIFICAR'
          ? { texto: 'transferencia por verificar', tono: 'tono-aviso' }
          : c.estadoTesoreria === 'NO_LLEGO' || c.estadoTesoreria === 'RECHAZADA'
            ? { texto: 'no llegó a tesorería', tono: 'tono-malo' }
            : c.estadoTesoreria
              ? { texto: 'recibido, falta abonar', tono: 'tono-aviso' }
              : { texto: 'por recibir en tesorería', tono: 'tono-info' };
  return (
    <Modal titulo={`Cobro · ${nota.numeroVisible} · ${nota.cliente}`} alCerrar={alCerrar}>
      {nota.cobros.length > 0 && (
        <table className="grilla compacta" aria-label="Cobros declarados">
          <tbody>
            {nota.cobros.map((c) => {
              const e = estado(c);
              return (
                <tr key={c.cobroId}>
                  <td>
                    {c.medio.toLowerCase()}
                    <span className="apagado"> · {c.origen === 'APP' ? 'app' : 'a mano'}</span>
                  </td>
                  <td className="der">{pesos(c.monto)}</td>
                  <td>
                    {c.medio === 'CHEQUE' && c.banco
                      ? `${c.banco} N° ${c.numeroDocumento ?? ''} · ${fecha(c.fechaDocumento)}`
                      : (c.referencia ?? '')}
                  </td>
                  <td>
                    <span className={`insignia ${e.tono}`}>{e.texto}</span>
                    {!c.anulado &&
                      !c.reciboId &&
                      (c.estadoTesoreria === 'RECIBIDO' || c.estadoTesoreria === 'VERIFICADA') &&
                      puede('cobranza.cobro.registrar') && (
                        <button
                          type="button"
                          className="boton-mini primario"
                          disabled={imputar.isPending}
                          onClick={() =>
                            imputar.mutate({ ruta: `cobros/${c.cobroId}/imputar`, cuerpo: {} })
                          }
                        >
                          Reintentar abono
                        </button>
                      )}
                    {registrable && !c.anulado && !c.reciboId && !c.estadoTesoreria && (
                      <button
                        type="button"
                        className="boton-mini"
                        disabled={anular.isPending}
                        onClick={() => {
                          const motivo = window.prompt('¿Por qué se anula este cobro?');
                          if (motivo && motivo.trim().length >= 3)
                            anular.mutate({
                              ruta: `cobros/${c.cobroId}/anular`,
                              cuerpo: { motivo: motivo.trim() },
                            });
                        }}
                      >
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {imputar.isError && <PanelError error={imputar.error} />}
      {anular.isError && <PanelError error={anular.error} />}
      {!registrable && (
        <div className="acciones-modal">
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
        </div>
      )}
      {registrable && (
        <>
          <p className="apagado">
            Rendición a mano: se anota lo que dice la hoja de rendición. Se abona al cliente cuando
            tesorería recibe los valores (Pago de Clientes › Recepción de rutas).
          </p>
          <label className="campo">
            <span className="etiqueta">Medio</span>
            <select
              aria-label="Medio de pago"
              value={medio}
              onChange={(e) => setMedio(e.target.value as typeof medio)}
            >
              <option value="EFECTIVO">Efectivo</option>
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="CHEQUE">Cheque</option>
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Monto (pesos)</span>
            <input
              aria-label="Monto"
              inputMode="numeric"
              autoFocus
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
            />
          </label>
          {medio === 'CHEQUE' ? (
            <div className="barra-filtros">
              <input
                aria-label="Banco del cheque"
                placeholder="Banco"
                value={cheque.banco}
                onChange={(e) => setCheque((x) => ({ ...x, banco: e.target.value }))}
              />
              <input
                aria-label="Número del cheque"
                placeholder="N° cheque"
                value={cheque.numeroDocumento}
                onChange={(e) => setCheque((x) => ({ ...x, numeroDocumento: e.target.value }))}
              />
              <input
                type="date"
                aria-label="Fecha del cheque"
                value={cheque.fechaDocumento}
                onChange={(e) => setCheque((x) => ({ ...x, fechaDocumento: e.target.value }))}
              />
            </div>
          ) : (
            medio === 'TRANSFERENCIA' && (
              <label className="campo">
                <span className="etiqueta">N° de operación</span>
                <input
                  aria-label="Referencia"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                />
              </label>
            )
          )}
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={alCerrar}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={!valor || accion.isPending}
              onClick={() =>
                accion.mutate(
                  {
                    ruta: 'cobros',
                    cuerpo: {
                      notaId: nota.notaId,
                      medio,
                      monto: valor,
                      referencia: referencia || null,
                      ...(medio === 'CHEQUE'
                        ? {
                            banco: cheque.banco.trim() || null,
                            numeroDocumento: cheque.numeroDocumento.trim() || null,
                            fechaDocumento: cheque.fechaDocumento || null,
                          }
                        : {}),
                    },
                  },
                  { onSuccess: alCerrar },
                )
              }
            >
              Registrar cobro
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
